import React, { forwardRef, useContext, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ButtonGroup, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { Replay, PlayArrow, Pause, SkipNext, Settings } from '@mui/icons-material';

import { BaseSection } from 'shared/sections/BaseSection/BaseSection';
import { SectionTitle } from 'shared/sections/SectionTitle/SectionTitle';
import { MenuItemElement } from 'contexts/LayoutContext/LayoutContext.types';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { SectionCTA } from 'shared/components/SectionCTA/SectionCTA';
import { FadeInOnScroll } from 'shared/components/FadeInOnScroll/FadeInOnScroll';
import { RaceAnimation, RaceAnimationHandle, SpeedMode } from 'shared/sections/WhySection/RaceAnimation';

const racerNames = ['Vibe Coding', 'Hiring Devs', 'Value Coding'];

export const WhySection = forwardRef<MenuItemElement>((props, ref) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { portfolioSectionRef, isMobile } = useContext(LayoutContext);
  const raceRef = useRef<RaceAnimationHandle>(null);
  const [raceAnimDone, setRaceAnimDone] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [maxStep, setMaxStep] = useState(10);
  const [isPaused, setIsPaused] = useState(false);
  const [speed, setSpeed] = useState<SpeedMode>('paced');
  const [highlight, setHighlight] = useState<number[] | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Poll the ref for state changes
  useEffect(() => {
    const interval = setInterval(() => {
      if (raceRef.current) {
        if (raceRef.current.animationDone && !raceAnimDone) setRaceAnimDone(true);
        setCurrentStep(raceRef.current.currentStep);
        setMaxStep(raceRef.current.maxStep);
        setIsPaused(raceRef.current.isPaused);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [raceAnimDone]);

  const handleRestart = () => { setRaceAnimDone(false); raceRef.current?.restart(); };
  const handlePlayPause = () => { isPaused ? raceRef.current?.play() : raceRef.current?.pause(); };
  const handleSkip = () => { raceRef.current?.skip(); };
  const handleSpeed = (s: SpeedMode) => { setSpeed(s); raceRef.current?.setSpeed(s); };
  const handleHighlight = (index: number) => {
    setHighlight(prev => {
      let next: number[] | null;
      if (prev === null) {
        // From "all", select just this one
        next = [index];
      } else if (prev.includes(index)) {
        // Remove this one
        const filtered = prev.filter(i => i !== index);
        next = filtered.length === 0 ? null : filtered;
      } else {
        // Add this one
        next = [...prev, index];
      }
      // If all selected, reset to null
      if (next && next.length === racerNames.length) next = null;
      raceRef.current?.setHighlight(next);
      return next;
    });
  };

  const handleHighlightAll = () => {
    setHighlight(null);
    raceRef.current?.setHighlight(null);
  };

  const handleDotClick = (step: number) => {
    raceRef.current?.pause();
    raceRef.current?.goToStep(step);
  };

  const btnSx = (active: boolean) => ({
    textTransform: 'none' as const,
    fontSize: isMobile ? 10 : 11,
    px: isMobile ? 1 : 1.5,
    py: 0.3,
    minWidth: 0,
    color: active ? 'white' : 'rgba(255,255,255,0.4)',
    borderColor: 'rgba(255,255,255,0.15)',
    backgroundColor: active ? 'rgba(99,101,255,0.3)' : 'transparent',
    '&:hover': { backgroundColor: 'rgba(99,101,255,0.2)', borderColor: 'rgba(255,255,255,0.25)' },
  });

  const dotSize = isMobile ? 8 : 10;
  const dotActiveSize = isMobile ? 11 : 14;
  const iconSize = isMobile ? 16 : 18;

  return (
    <BaseSection
      ref={ref}
      style={{
        padding: 12,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, rgba(7, 7, 8, 0) 0%, #070708 15.1%, #070708 85.42%, rgba(7, 7, 8, 0) 100%)'
          : 'linear-gradient(180deg, rgba(7, 7, 8, 0) 0%, #ececec 15.1%, #ececec 85.42%, rgba(7, 7, 8, 0) 100%)',
      }}
    >
      <FadeInOnScroll>
        <SectionTitle onEnterViewport={() => navigate('/why')}>
          Why choose us?
        </SectionTitle>
      </FadeInOnScroll>
      <RaceAnimation ref={raceRef}/>

      {/* Controls row */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        mt: -6,
        mb: 1,
        position: 'relative',
        zIndex: 10,
        flexWrap: 'wrap',
      }}>
        {/* Playback controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={<Typography variant="body2">Restart</Typography>}>
            <IconButton onClick={handleRestart} size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
              <Replay sx={{ width: iconSize, height: iconSize }}/>
            </IconButton>
          </Tooltip>
          {!raceAnimDone && (
            <Tooltip title={<Typography variant="body2">{isPaused ? 'Play' : 'Pause'}</Typography>}>
              <IconButton onClick={handlePlayPause} size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
                {isPaused ? <PlayArrow sx={{ width: iconSize, height: iconSize }}/> : <Pause sx={{ width: iconSize, height: iconSize }}/>}
              </IconButton>
            </Tooltip>
          )}
          {!raceAnimDone && (
            <Tooltip title={<Typography variant="body2">Skip to End</Typography>}>
              <IconButton onClick={handleSkip} size="small" sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
                <SkipNext sx={{ width: iconSize, height: iconSize }}/>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={<Typography variant="body2">Settings</Typography>}>
            <IconButton onClick={() => setShowSettings(s => !s)} size="small" sx={{ color: showSettings ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>
              <Settings sx={{ width: iconSize, height: iconSize }}/>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Step dots */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
          {Array.from({ length: maxStep + 1 }, (_, i) => (
            <Box
              key={i}
              onClick={() => handleDotClick(i)}
              sx={{
                width: i === currentStep ? dotActiveSize : dotSize,
                height: i === currentStep ? dotActiveSize : dotSize,
                borderRadius: '50%',
                backgroundColor: i <= currentStep ? '#6365FF' : 'rgba(255,255,255,0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                zIndex: 10,
                '&:hover': { backgroundColor: '#6365FF', transform: 'scale(1.4)' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Settings panel: speed + highlight */}
      {showSettings && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mb: 1,
          flexWrap: 'wrap',
          zIndex: 10,
        }}>
          <ButtonGroup size="small" variant="outlined">
            {([['slow', 'Slow'], ['paced', 'Paced'], ['fast', 'Fast']] as [SpeedMode, string][]).map(([s, label]) => (
              <Button key={s} onClick={() => handleSpeed(s)} sx={btnSx(speed === s)}>
                {label}
              </Button>
            ))}
          </ButtonGroup>

          <ButtonGroup size="small" variant="outlined">
            <Button onClick={handleHighlightAll} sx={btnSx(highlight === null)}>
              All
            </Button>
            {racerNames.map((name, i) => (
              <Button key={i} onClick={() => handleHighlight(i)} sx={btnSx(highlight !== null && highlight.includes(i))}>
                {name}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}

      <FadeInOnScroll delay={0.1}>
        <SectionCTA
          nextSectionLabel="Our Proven Outcomes"
          onNextSection={() => portfolioSectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}
        />
      </FadeInOnScroll>
    </BaseSection>
  );
});
